import React from 'react'
import { cn } from './cn'

// `title` 在這裡是分節標題（ReactNode），不是 HTML 的 title tooltip 屬性，所以把原生的
// 那個 Omit 掉；分節不需要 tooltip。
export interface NumberedSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** Serif index shown in the left rail, e.g. "01" (decorative — heading carries the a11y name). */
  number?: React.ReactNode
  /** Section heading (rendered as an h2). */
  title: React.ReactNode
  /** Optional muted sub-line under the heading. */
  description?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * 編號分段：左欄 serif 大數字（01/02…）＋ 右欄標題／描述／內容，段與段之間髮絲線分層。
 * settings v4 prototype 的招牌版式，app shell 內唯一的分節語彙（桌面設定頁與 web 共用）。
 * 數字純裝飾（aria-hidden），語意標題由 h2 承載。版面全部走 Tailwind token utility。
 * 內部間距照 design-guideline §3.3：標題→說明 --gap-xs、說明／標題→內容 --gap-md。
 * `...rest` 透傳到 <section>（data-testid、style…），className 仍經過 cn 合併。
 */
export function NumberedSection({
  number,
  title,
  description,
  children,
  className,
  ...rest
}: NumberedSectionProps) {
  return (
    <section
      data-numbered-section={typeof number === 'string' || typeof number === 'number' ? String(number) : ''}
      {...rest}
      className={cn(
        // 左欄固定 56px（設計稿 .num-sec 的欄寬）：用 auto 會隨數字字寬變，各節標題左緣會飄。
        'grid grid-cols-[56px_minmax(0,1fr)] gap-[var(--gap-md)] border-t border-border py-[22px] first:border-t-0 max-sm:grid-cols-[minmax(0,1fr)]',
        className
      )}
    >
      {/* 序號字級走 --fs-index-lg（編號分段序號的 token），不是內文的 3xl 階。 */}
      <div
        className="font-display text-[length:var(--fs-index-lg)] leading-none text-muted max-sm:hidden"
        aria-hidden="true"
      >
        {number}
      </div>
      <div className="min-w-0">
        <header className="mb-[var(--gap-md)]">
          <h2
            className={cn(
              // 分節標題是高密度資訊標題，走 sans（design-guideline §4.4）；
              // 不寫 font-sans 會吃到 kiao-app.css 全域 h2 的 --font-display 襯線。
              'font-sans text-lg font-semibold tracking-tight',
              description != null && 'mb-[var(--gap-xs)]'
            )}
          >
            {title}
          </h2>
          {description != null ? <p className="text-xs text-muted">{description}</p> : null}
        </header>
        {children}
      </div>
    </section>
  )
}
